import React, {Component} from 'react';
import Drawer from '@material-ui/core/Drawer';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import Alert from '@material-ui/lab/Alert';
import Button from '../../../components/button';
import AccessRefusal from '../../../components/access-refusal';

// Which scope TYPES belong to each chain — mirrors accessEvaluator's CHAIN_LEVELS, needed to know
// which /access/scopes?type= results feed which chain's picker.
const CHAIN_SCOPE_TYPES = {platform: ['connection', 'context'], datadictionary: ['instance', 'schema']};

// The chain, in the words the mockup uses. "platform" and "datadictionary" are the model's names
// for the two trees; nobody administering access thinks in them.
const CHAIN_LABEL = {
  platform: 'Connections & environments',
  datadictionary: 'Data dictionary',
  global: 'Everywhere',
};

const CHAIN_QUESTION = {
  platform: 'Which systems may they connect to?',
  datadictionary: 'Which catalogue content may they import and refresh?',
};

// The scope trail under each picker — `global › context · Pensions › connection × 4`. Ordered
// broadest-first, which is the opposite of `CHAIN_LEVELS` (leaf-first, for the upward walk `can()`
// does); a trail reads top-down and the evaluator resolves bottom-up.
const CHAIN_TRAIL = {
  platform: ['global', 'context', 'connection'],
  datadictionary: ['global', 'instance', 'schema', 'object'],
};

// The per-code error map that used to live here moved to `components/access-refusal.js` (slice
// 4b-2 §3.9 — mockup S8). It is the same wire codes and the same fallback; one component so a
// refusal reads the same wherever it surfaces, and so the "what to do next" wording the mockup
// asks for lives in one place rather than being reinvented per call site.

// A retired (is_active: 0) role offered here would grant nothing on submit — createAssignment
// looks it up with `AND is_active = 1` and 400s GRANT_CONFERS_NOTHING, which reads like a chain
// problem rather than "this role no longer exists". `getRoleCatalog` itself is not filtered
// server-side: `listAssignments` reuses it to label HISTORICAL assignments, which must keep
// showing a retired role's name, so the filter belongs only here, at the picker.
const activeRoles = (roles) => (roles || []).filter((r) => r.is_active);

// Tomorrow, local time, as the `type="date"` input's own `min` — picking TODAY sends local
// midnight, which 4a's `expiresAt <= new Date()` check refuses as already past. Built from local
// getters (getFullYear/getMonth/getDate), NOT `toISOString()`: that converts to UTC first, which
// is the wrong day for any timezone behind UTC at the moment this runs.
const minExpiry = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * `POST /access/assignments` (docs/access/slice-4b-admin-surface-plan.md §5). The role selector
 * expands to show chains + permissions (`ac-role-row`) — there is no separate role-list panel
 * because that would be a fourth panel over the same `GET /access/roles` call.
 *
 * `object` scope is not offered (§5): the long tail, and a picker over every table needs search
 * this drawer does not have. Grants land at schema and above.
 */
class AssignmentDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = this.emptyState();
    this.lastBody = null;
  }

  emptyState() {
    return {roleCode: '', globalScope: false, chainSelection: {}, expiresAt: '', note: ''};
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      ['instance', 'schema', 'connection', 'context'].forEach((type) => this.props.onLoadScopes(type));
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.emptyState());
    }
  }

  selectedRole() {
    return activeRoles(this.props.roles).find((r) => r.code === this.state.roleCode) || null;
  }

  requestedChains() {
    const role = this.selectedRole();
    return role ? role.chains.filter((c) => c !== 'global') : [];
  }

  handleRoleChange = (event) => {
    const roleCode = event.target.value;
    const role = activeRoles(this.props.roles).find((r) => r.code === roleCode);
    // A role carrying a global-only code (e.g. audit:read) can ONLY be conferred by a {type:
    // 'global'} scope — createAssignment's chain-mismatch check refuses anything else outright.
    const requiresGlobal = Boolean(role && role.chains.includes('global'));
    this.setState({roleCode, globalScope: requiresGlobal, chainSelection: {}});
  };

  handleChainScopeChange = (chain, event) => {
    const value = event.target.value ? JSON.parse(event.target.value) : null;
    this.setState((s) => ({chainSelection: {...s.chainSelection, [chain]: value}}));
  };

  optionsForChain(chain) {
    const {scopeOptions} = this.props;
    return (CHAIN_SCOPE_TYPES[chain] || [])
        .flatMap((type) => (scopeOptions[type] || []).map((o) => ({...o, type})));
  }

  buildScopes() {
    if (this.state.globalScope) return [{type: 'global'}];
    return this.requestedChains().map((c) => this.state.chainSelection[c]).filter(Boolean);
  }

  canSubmit() {
    if (!this.selectedRole()) return false;
    if (this.state.globalScope) return true;
    return this.requestedChains().every((c) => this.state.chainSelection[c]);
  }

  /**
   * S2's "What this will grant" — the permissions this role confers, grouped by chain, against the
   * scope chosen for that chain.
   *
   * Uses `permissionDetail` (per-code chain), NOT `role.chains`: that is a set for the WHOLE role
   * and cannot say which side of the split any one permission falls on, which is the entire
   * grouping the panel is made of.
   *
   * @return {Array<{chain: string, scope: object, permissions: Array}>} One group per chain.
   */
  previewGroups() {
    const role = this.selectedRole();
    if (!role) return [];
    const detail = role.permissionDetail || [];
    const chains = [...new Set(detail.map((p) => p.chain).filter(Boolean))].sort();
    return chains.map((chain) => ({
      chain,
      // NAMED, not `{type, id}`. `chainSelection` holds only what the request body needs, so a
      // preview reading `.name` off it renders `instance:inst-test` — a uuid-shaped id on the panel
      // whose entire job is telling an admin what they are about to grant, and where.
      scope: chain === 'global' || this.state.globalScope ?
        {type: 'global', name: 'everywhere'} : this.namedScope(chain),
      permissions: detail.filter((p) => p.chain === chain),
    }));
  }

  /**
   * The chosen scope for a chain, with its display name attached.
   *
   * @param {string} chain `platform` or `datadictionary`.
   * @return {?object} `{type, id, name}`, or null when nothing is chosen yet.
   */
  namedScope(chain) {
    const chosen = this.state.chainSelection[chain];
    if (!chosen) return null;
    const option = this.optionsForChain(chain).find(
        (o) => o.type === chosen.type && String(o.id) === String(chosen.id));
    return {...chosen, name: option ? `${chosen.type} ${option.name}` : `${chosen.type}:${chosen.id}`};
  }

  /**
   * S2's "Blocked — this would grant nothing": a chain the role covers with no scope chosen for it.
   *
   * The mockup's case is `data_custodian`, which spans connections AND the data dictionary. Pick a
   * connection scope and leave the catalogue scope empty and the grant looks like it works while
   * conferring only half the role — the two trees are not linked.
   *
   * The server refuses this outright with GRANT_CONFERS_NOTHING (4a), and that stays the backstop.
   * This exists so the answer arrives BEFORE submit, naming the chain that is missing.
   *
   * @return {Array<string>} Chains with no scope chosen.
   */
  blockedChains() {
    if (!this.selectedRole() || this.state.globalScope) return [];
    return this.requestedChains().filter((c) => !this.state.chainSelection[c]);
  }

  handleSubmit = () => {
    const body = {
      subjectId: this.props.subjectId,
      roleCode: this.state.roleCode,
      scopes: this.buildScopes(),
      note: this.state.note || undefined,
    };
    if (this.state.expiresAt) body.expiresAt = `${this.state.expiresAt}T00:00:00`;
    this.lastBody = body;
    this.props.onSubmit(body, this.props.subjectId);
  };

  handleRetry = () => {
    if (this.lastBody) this.props.onSubmit(this.lastBody, this.props.subjectId);
  };

  /**
   * The mockup's scope trail — `global › context · Pensions › connection × 4`.
   *
   * It is the element that makes the two-tree split legible. Without it the form is two dropdowns
   * with no indication that they address different hierarchies, which is the "silent failure" the
   * screen's own caption says it exists to stop.
   *
   * @param {string} chain `platform` or `datadictionary`.
   * @return {Array<{text: string, strong: boolean}>} Trail segments, leaf emphasised.
   */
  scopeTrail(chain) {
    const levels = CHAIN_TRAIL[chain] || [];
    const chosen = this.state.chainSelection[chain];
    if (!chosen) return levels.map((l) => ({text: l, strong: false}));

    const option = this.optionsForChain(chain).find(
        (o) => o.type === chosen.type && String(o.id) === String(chosen.id));
    const name = option ? option.name : chosen.id;

    return levels.map((level) => (level === chosen.type ?
      {text: `${level} · ${name}`, strong: true} :
      {text: level, strong: false}));
  }

  render() {
    const {open, onClose, roles, grantError, subjectName} = this.props;
    const role = this.selectedRole();

    return (
      <Drawer anchor="right" open={open} onClose={onClose} classes={{paper: 'grant-drawer-paper'}}>
        {/*
          TWO COLUMNS, matching mockup S2: the form on the left, "What this will grant" and the
          Blocked panel on the right. A first version stacked the preview under the form, which
          meant the consequence of a choice sat below the fold at the moment it was made — the
          preview exists to be read WHILE choosing scopes, not after scrolling past them.
        */}
        <div className="grant-drawer grant-two-col">
          <div className="grant-form-col">
            <h3>Assign access</h3>
            <div className="grant-subtitle">
              One decision, recorded once — expanded internally to one grant per tree
            </div>

            {/* S8's refusal copy, wired at a real call site rather than shown as a specimen on a
                page. Replaces this drawer's own inline ERROR_MESSAGE map. */}
            {grantError && (
              <AccessRefusal error={grantError}>
                {grantError.code === 'ACCESS_WRITE_BUSY' && (
                  <Button label="Retry" className="btn btn-default" onClick={this.handleRetry} />
                )}
              </AccessRefusal>
            )}

            <div className="grant-card">
              <div className="grant-card-head">
                Assign a role
                {subjectName && <span className="grant-card-sub"> — to {subjectName}</span>}
              </div>

              <TextField
                select fullWidth label="Role" value={this.state.roleCode} onChange={this.handleRoleChange}
                SelectProps={{native: true}}
                inputProps={{'data-testid': 'ac-grant-role'}}
              >
                <option value="" />
                {activeRoles(roles).map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
              </TextField>

              {/* The role's DESCRIPTION, as drawn — "Connect to source systems, run imports,
                  refreshes and monitors." A list of permission codes answers a different question
                  and answers it worse at the moment of choosing. */}
              {role && (
                <div className="role-detail" data-testid="ac-role-row">
                  <div className="role-description">{role.description}</div>
                  <div className="role-chains">
                    {role.chains.map((c) => <Chip key={c} size="small" label={c} />)}
                  </div>
                </div>
              )}

              {role && role.chains.includes('global') && (
                <div className="form-hint">
                  This role includes global-only permissions, so it can only be granted at global scope.
                </div>
              )}

              {role && !this.state.globalScope && this.requestedChains().map((chain) => (
                <div key={chain} className="scope-block" data-testid={`ac-scope-block-${chain}`}>
                  <div className="scope-block-title">Scope — {CHAIN_LABEL[chain] || chain}</div>
                  <div className="scope-block-question">{CHAIN_QUESTION[chain] || ''}</div>
                  <TextField
                    select fullWidth
                    value={this.state.chainSelection[chain] ? JSON.stringify(this.state.chainSelection[chain]) : ''}
                    onChange={(event) => this.handleChainScopeChange(chain, event)}
                    SelectProps={{native: true}}
                    inputProps={{'data-testid': `ac-grant-scope-${chain}`}}
                  >
                    <option value="" />
                    {this.optionsForChain(chain).map((o) => (
                      <option
                        key={`${o.type}:${o.id}`} value={JSON.stringify({type: o.type, id: o.id})}
                        data-testid="ac-scope-option"
                      >
                        {o.type}: {o.name}
                      </option>
                    ))}
                  </TextField>
                  <div className="scope-trail" data-testid={`ac-scope-trail-${chain}`}>
                    {this.scopeTrail(chain).map((seg, i) => (
                      <span key={seg.text}>
                        {i > 0 && <span className="trail-sep"> › </span>}
                        <span className={seg.strong ? 'trail-strong' : ''}>{seg.text}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}

              {role && !role.chains.includes('global') && (
                <label className="global-toggle">
                  <input
                    type="checkbox" checked={this.state.globalScope}
                    onChange={(event) => this.setState({globalScope: event.target.checked})}
                  />
                  Grant at global scope instead
                </label>
              )}

              <TextField
                label="Expires" type="date" value={this.state.expiresAt}
                onChange={(event) => this.setState({expiresAt: event.target.value})}
                InputLabelProps={{shrink: true}}
                inputProps={{min: minExpiry()}}
              />
              {/* Ties this form to S8's review, which is where a permanent grant surfaces. */}
              <div className="form-hint">
                Leave blank for permanent access — permanent grants are flagged in the monthly
                review after 180 days.
              </div>

              <TextField
                label="Note" value={this.state.note}
                onChange={(event) => this.setState({note: event.target.value})}
              />

              <div className="grant-actions">
                <Button label="Cancel" className="btn btn-default" onClick={onClose} />
                <Button
                  label="Assign" className="btn btn-action" disabled={!this.canSubmit()}
                  onClick={this.handleSubmit} data-testid="ac-grant-submit"
                />
              </div>
            </div>
          </div>

          <div className="grant-preview-col">
            {/* Without this the form asks for scopes without ever saying what they buy, and the
                two-tree split is invisible until something confers nothing. */}
            {role && (
              <div className="grant-preview" data-testid="ac-grant-preview">
                <h4>What this will grant</h4>
                {this.previewGroups().map((g) => (
                  <div key={g.chain} className="preview-chain" data-testid="ac-preview-chain">
                    <div className="preview-chain-name">{CHAIN_LABEL[g.chain] || 'Everywhere'}</div>
                    <ul className="preview-permissions">
                      {g.permissions.map((p) => (
                        <li key={p.code} data-testid="ac-preview-permission">
                          <span className="preview-code">
                            {p.is_sensitive && <span title="Sensitive">🔒 </span>}{p.code}
                          </span>
                          <span className="preview-where">
                            {g.scope ? (g.scope.name || `${g.scope.type}:${g.scope.id}`) :
                              'no scope chosen'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {!!this.blockedChains().length && (
              <Alert severity="error" className="grant-blocked" data-testid="ac-grant-blocked">
                <strong>Blocked — this would grant nothing.</strong>
                {` You chose a scope on one tree but left ${this.blockedChains()
                    .map((c) => CHAIN_LABEL[c] || c).join(' and ')} empty. `}
                {`${role.name} also carries permissions there, and a scope on one tree does not
                  reach the other — the two are not linked. Pick a scope for each, or choose a role
                  that covers only one.`}
              </Alert>
            )}

            {role && (
              <div className="preview-caption">
                This is what the form does when it would otherwise create an assignment that
                <em> looks </em> like it works and doesn&apos;t.
              </div>
            )}
          </div>
        </div>
      </Drawer>
    );
  }
}

export default AssignmentDrawer;
